import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { useAccount } from '@/api/useAccount';
import {
  AccountTermsRefusedError,
  TERMS_FIELDS,
  TERMS_MAX_LENGTH,
  useAccountTerms,
  useFetchTermsSample,
  useUpdateAccountTerms,
  type AccountTerms,
  type TermsField,
} from '@/api/useAccountTerms';
import { formatInstantWithZone } from '@/lib/format';
import { cn } from '@/lib/utils';

type Values = Record<TermsField, string>;

function stored(terms: AccountTerms): Values {
  return {
    estimateTerms: terms.estimateTerms ?? '',
    invoiceTerms: terms.invoiceTerms ?? '',
    warrantyStatement: terms.warrantyStatement ?? '',
  };
}

/** As the API measures a box: line endings as `\n`, the whole trimmed (`AccountTerms.Normalise`). */
function measured(text: string): number {
  return text.replace(/\r\n?/g, '\n').trim().length;
}

const TEXTAREA_CLASS =
  'w-full rounded-md border bg-transparent px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';

/**
 * The shop's own document terms (M2-48, Kyle 2026-10-24): estimate terms,
 * invoice terms and a warranty statement, plain text with line breaks kept,
 * up to 10,000 characters each. Owner and Admin (`company.manage`, the
 * settings route's gate).
 *
 * - **Every save is a new version, and only the future changes.** A document
 *   already sent keeps the terms it went out with (the API captures them at
 *   send), so the section says so rather than letting someone think an edit
 *   fixes what a customer already has.
 * - **The sample is opt-in, never pre-filled** (Q11-B). "Start from the
 *   sample" reads the API's wording, headed "SAMPLE: review with your attorney
 *   before using", into that one box — after asking, if the box has text.
 * - **No `maxLength`.** A paste over the limit would be cut without a word;
 *   the count turns red and Save is held instead (M2-07's notes).
 * - Saves land in Recent changes as a `Terms` row.
 */
export function DocumentTermsSection() {
  const { t } = useTranslation();
  const id = useId();
  const terms = useAccountTerms();

  return (
    <section
      className="space-y-3 rounded-lg border p-4"
      aria-labelledby={`${id}-heading`}
      data-testid="terms-section"
    >
      <div>
        <h2 id={`${id}-heading`} className="text-base font-semibold">
          {t('settings.terms.heading')}
        </h2>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('settings.terms.description')}
        </p>
      </div>
      {terms.isPending ? (
        <LoadingState label={t('settings.terms.loading')} />
      ) : terms.isError ? (
        <ErrorState
          title={t('settings.terms.loadErrorTitle')}
          onRetry={() => void terms.refetch()}
        />
      ) : (
        <TermsForm terms={terms.data} headingId={`${id}-heading`} />
      )}
    </section>
  );
}

function TermsForm({ terms, headingId }: { terms: AccountTerms; headingId: string }) {
  const { t } = useTranslation();
  const id = useId();
  const save = useUpdateAccountTerms();
  const fetchSample = useFetchTermsSample();
  const timeZone = useAccount().data?.timezone;

  // The form follows what is stored, in place (CompanySettings' useStoredForm,
  // and its reason: a remount would drop focus from Save after every save).
  const storedValues = stored(terms);
  const storedKey = JSON.stringify(storedValues);
  const [values, setValues] = useState(storedValues);
  const [baseline, setBaseline] = useState(storedKey);
  if (storedKey !== baseline) {
    setBaseline(storedKey);
    setValues(storedValues);
  }
  const dirty = JSON.stringify(values) !== storedKey;

  const [saved, setSaved] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [sampleError, setSampleError] = useState<string | null>(null);
  const [sampleLoading, setSampleLoading] = useState<TermsField | null>(null);
  const [confirmSample, setConfirmSample] = useState<TermsField | null>(null);
  const boxes = useRef<Partial<Record<TermsField, HTMLTextAreaElement | null>>>({});
  const saveButton = useRef<HTMLButtonElement>(null);
  // The browser blurs Save while it is disabled for the request (seen in
  // Chromium at 375px), and a disabled button cannot take focus back: so once
  // the save has settled, put focus back on it — only if it was dropped.
  const [refocusSave, setRefocusSave] = useState(false);
  useEffect(() => {
    if (!refocusSave || save.isPending) return;
    const active = document.activeElement;
    if (active === null || active === document.body) saveButton.current?.focus();
    setRefocusSave(false);
  }, [refocusSave, save.isPending]);

  const label = (field: TermsField) => t(`settings.terms.field.${field}`);
  const over = TERMS_FIELDS.filter((f) => measured(values[f]) > TERMS_MAX_LENGTH);
  const serverErrors = save.error instanceof AccountTermsRefusedError ? save.error.fields : {};
  const general =
    save.isError && !(save.error instanceof AccountTermsRefusedError)
      ? t('settings.company.saveFailed')
      : null;

  function edit(field: TermsField, text: string) {
    setValues((prev) => ({ ...prev, [field]: text }));
    setSaved(null);
  }

  async function fillSample(field: TermsField) {
    setConfirmSample(null);
    setSampleError(null);
    setSampleLoading(field);
    try {
      const sample = await fetchSample();
      edit(field, sample[field]);
      setAnnouncement(t('settings.terms.sampleAdded', { field: label(field) }));
      boxes.current[field]?.focus();
    } catch {
      setSampleError(t('settings.terms.sampleFailed'));
    } finally {
      setSampleLoading(null);
    }
  }

  function askSample(field: TermsField) {
    if (values[field].trim() !== '') setConfirmSample(field);
    else void fillSample(field);
  }

  function discard() {
    setValues(storedValues);
    setSaved(null);
    save.reset();
    boxes.current.estimateTerms?.focus();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (over.length > 0) return;
    setSaved(null);
    const body = Object.fromEntries(
      TERMS_FIELDS.map((f) => [f, values[f].trim() === '' ? null : values[f]]),
    ) as Record<TermsField, string | null>;
    save.mutate(body, {
      onSuccess: () => {
        setSaved(t('settings.terms.saved'));
        setRefocusSave(true);
      },
    });
  }

  return (
    <form className="space-y-4" onSubmit={submit} aria-labelledby={headingId}>
      <p className="text-sm" data-testid="terms-current-version">
        {terms.version != null && terms.savedAt
          ? terms.savedBy
            ? t('settings.terms.versionBy', {
                version: Number(terms.version),
                when: formatInstantWithZone(terms.savedAt, timeZone),
                who: terms.savedBy,
              })
            : t('settings.terms.versionOn', {
                version: Number(terms.version),
                when: formatInstantWithZone(terms.savedAt, timeZone),
              })
          : t('settings.terms.none')}
      </p>

      {TERMS_FIELDS.map((field) => {
        const used = measured(values[field]);
        const tooLong = used > TERMS_MAX_LENGTH;
        const error = tooLong
          ? t('settings.terms.tooLong', {
              field: label(field),
              max: TERMS_MAX_LENGTH.toLocaleString(),
            })
          : serverErrors[field];
        return (
          <div key={field} className="space-y-1" data-testid={`terms-${field}`}>
            <div className="flex flex-wrap items-end justify-between gap-2">
              <label htmlFor={`${id}-${field}`} className="block text-sm font-medium">
                {label(field)}
              </label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={sampleLoading !== null || save.isPending}
                aria-describedby={`${id}-${field}-sample-hint`}
                onClick={() => askSample(field)}
              >
                {sampleLoading === field
                  ? t('settings.terms.sampleLoading')
                  : t('settings.terms.useSample')}
              </Button>
              <span id={`${id}-${field}-sample-hint`} className="sr-only">
                {t('settings.terms.sampleHint', { field: label(field) })}
              </span>
            </div>
            <textarea
              id={`${id}-${field}`}
              ref={(el) => {
                boxes.current[field] = el;
              }}
              rows={6}
              className={cn(
                TEXTAREA_CLASS,
                error ? 'border-[var(--color-destructive)]' : 'border-[var(--color-input)]',
              )}
              value={values[field]}
              aria-invalid={error ? true : undefined}
              aria-describedby={[`${id}-${field}-count`, error ? `${id}-${field}-error` : null]
                .filter(Boolean)
                .join(' ')}
              onChange={(e) => edit(field, e.target.value)}
            />
            <p
              id={`${id}-${field}-count`}
              className={cn(
                'text-right text-xs tabular-nums',
                tooLong
                  ? 'text-[var(--color-destructive)]'
                  : 'text-[var(--color-muted-foreground)]',
              )}
              data-testid={`terms-${field}-count`}
            >
              {t('settings.terms.count', {
                used: used.toLocaleString(),
                max: TERMS_MAX_LENGTH.toLocaleString(),
              })}
            </p>
            {error ? (
              <p
                id={`${id}-${field}-error`}
                className="text-sm text-[var(--color-destructive)]"
                data-testid={`terms-${field}-error`}
              >
                {error}
              </p>
            ) : null}
          </div>
        );
      })}

      {sampleError ? (
        <p
          role="alert"
          className="text-sm text-[var(--color-destructive)]"
          data-testid="terms-sample-error"
        >
          {sampleError}
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <Button
          ref={saveButton}
          type="submit"
          size="sm"
          disabled={save.isPending || over.length > 0}
        >
          {t('settings.terms.save')}
        </Button>
        {dirty ? (
          <Button type="button" size="sm" variant="outline" onClick={discard}>
            {t('settings.company.discard')}
          </Button>
        ) : null}
      </div>
      <p role="status" aria-live="polite" className="text-sm text-[var(--color-muted-foreground)]">
        {saved ?? ''}
      </p>
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      {general || Object.keys(serverErrors).length > 0 ? (
        <p
          role="alert"
          className="text-sm text-[var(--color-destructive)]"
          data-testid="terms-save-error"
        >
          {general ?? t('settings.terms.refused')}
        </p>
      ) : null}

      {confirmSample ? (
        <ConfirmDialog
          titleId={`${id}-sample-title`}
          testId="terms-sample-confirm"
          title={t('settings.terms.sampleConfirmTitle', { field: label(confirmSample) })}
          description={t('settings.terms.sampleConfirmDescription')}
          confirmLabel={t('settings.terms.sampleConfirm')}
          destructive={false}
          onConfirm={() => void fillSample(confirmSample)}
          onClose={() => setConfirmSample(null)}
        />
      ) : null}
    </form>
  );
}
